import React from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';
import * as Sentry from '@sentry/react';
import { router } from './router.tsx';
import { FullScreenLoader } from '@/features/ui/components/FullScreenLoader';
import { logger } from '@/features/help/utils/logger';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.MODE || 'development';
const isProduction = environment === 'production';
const isStaging = environment === 'staging';
const shouldEnhanceLogging = isProduction || isStaging;

console.log(
  `[Sentry Init] Enhanced logging ${shouldEnhanceLogging ? 'ENABLED' : 'DISABLED'} for environment: ${environment}`
);

Sentry.init({
  dsn: sentryDsn,
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  tracesSampleRate: isProduction ? 0.1 : isStaging ? 0.5 : 1.0,
  // Session Replay sampling rates
  replaysSessionSampleRate: isProduction ? 0.1 : isStaging ? 0.5 : 1.0,
  replaysOnErrorSampleRate: 1.0,
  ...(shouldEnhanceLogging && {
    _experiments: {
      enableLogs: true, // Only enable structured logs in production/staging
    },
  }),
});

// Global unhandled Promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  const isTimeoutError = 
    (typeof error === 'string' && error.toLowerCase().includes('timeout')) ||
    (error instanceof Error && error.message.toLowerCase().includes('timeout'));

  logger.error('Global Handler', 'Unhandled Promise rejection detected', {
    reason: error,
    isTimeoutError,
    reasonType: typeof error,
    stack: error instanceof Error ? error.stack : undefined,
    url: window.location.href,
    timestamp: new Date().toISOString(),
  });

  // Report to Sentry with additional context
  Sentry.captureException(
    error instanceof Error ? error : new Error(String(error)),
    {
      tags: {
        errorType: 'unhandled-promise-rejection',
        isTimeout: isTimeoutError,
      },
      extra: {
        originalReason: error,
        reasonType: typeof error,
        url: window.location.href,
      },
    }
  );

  // Prevent the browser from logging the error to console if it's a timeout
  // (since we've already logged it properly)
  if (isTimeoutError) {
    event.preventDefault();
  }
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>
);
